import React, { memo } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { motion } from "framer-motion";
import styled from "@emotion/styled";
import { GitBranch, AlertCircle, Link2, ArrowRight } from "lucide-react";
import { ServiceNode as ServiceNodeType } from "../../types/diagram.types";
import { useDiagramStore } from "../../store/diagramStore";
import { useTheme } from "../../context/ThemeContext";

const NodeContainer = styled(motion.div)<{
  $isSelected: boolean;
  $isHighlighted: boolean;
  $isHovered: boolean;
  $isDarkMode: boolean;
}>`
  background: ${(props) => {
    if (props.$isDarkMode) {
      return "linear-gradient(135deg, #f59e0b 0%, #eab308 100%)";
    } else {
      return "linear-gradient(135deg, #fb6340 0%, #fbb140 100%)";
    }
  }};
  border-radius: 12px;
  padding: 16px;
  min-width: 250px;
  max-width: 350px;
  box-shadow: ${(props) => (props.$isSelected ? "0 0 0 3px rgba(94, 114, 228, 0.5), 0 10px 30px rgba(0,0,0,0.15)" : props.$isHovered ? "0 10px 30px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.08)")};
  border: 2px solid ${(props) => (props.$isSelected ? "#5e72e4" : "transparent")};
  opacity: ${(props) => (props.$isHighlighted ? 1 : 0.98)};
  transform: ${(props) => (props.$isHighlighted ? "scale(1.05)" : "scale(1)")};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: white;
`;

const NodeTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const NodeBody = styled.div`
  color: white;
  font-size: 12px;
`;

const RouteList = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const RouteItem = styled.div<{ $isPaused?: boolean; $isHighlighted?: boolean }>`
  padding: 8px;
  margin: 6px 0;
  background: ${(props) => (props.$isHighlighted ? "rgba(94, 114, 228, 0.4)" : "rgba(255, 255, 255, 0.2)")};
  border-radius: 6px;
  font-size: 11px;
  opacity: ${(props) => (props.$isPaused ? 0.5 : 1)};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  border: ${(props) => (props.$isHighlighted ? "2px solid rgba(94, 114, 228, 0.8)" : "2px solid transparent")};
  transition: all 0.3s ease;
  box-shadow: ${(props) => (props.$isHighlighted ? "0 0 12px rgba(94, 114, 228, 0.4)" : "none")};
`;

const RouteInfo = styled.div`
  flex: 1;
`;

const RouteName = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const RouteTarget = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
`;

const LinkedIndicator = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #4caf50;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

interface CustomNodeData extends ServiceNodeType {
  isConnectable?: boolean;
}

export const ConvoyNodeComponent: React.FC<NodeProps<CustomNodeData>> = memo(({ data, isConnectable, selected }) => {
  const { hoveredNodeId, highlightedPath, highlightedRoutes, setHoveredNode, highlightPath, clearHighlight } = useDiagramStore();
  const { isDarkMode } = useTheme();

  const isHovered = hoveredNodeId === data.id;
  const isHighlighted = highlightedPath.includes(data.id);

  const handleMouseEnter = () => {
    setHoveredNode(data.id);
    highlightPath(data.id);
  };

  const handleMouseLeave = () => {
    setHoveredNode(null);
    clearHighlight();
  };

  // Create a handle ID for each route
  const getRouteHandleId = (routeId: string) => `route-${routeId}`;

  return (
    <>
      {/* Target handles on all sides for better attachment */}
      <Handle
        id="target-left"
        type="target"
        position={Position.Left}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          top: "45%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="target-right"
        type="target"
        position={Position.Right}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          top: "45%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="target-top"
        type="target"
        position={Position.Top}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          left: "45%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="target-bottom"
        type="target"
        position={Position.Bottom}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          left: "45%",
        }}
        isConnectable={isConnectable}
      />

      <NodeContainer $isSelected={!!selected} $isHighlighted={isHighlighted} $isHovered={isHovered} $isDarkMode={isDarkMode} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}>
        {data.linkedDiagramId && (
          <LinkedIndicator title="Linked to another diagram">
            <Link2 size={14} color="white" />
          </LinkedIndicator>
        )}

        <NodeHeader>
          <GitBranch size={20} color="white" />
          <NodeTitle>{data.name}</NodeTitle>
        </NodeHeader>

        <NodeBody>
          {data.description && <div style={{ marginBottom: 8, opacity: 0.9 }}>{data.description}</div>}

          <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 8 }}>Webhook Router System</div>

          {data.convoyRoutes && data.convoyRoutes.length > 0 && (
            <RouteList>
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 12 }}>Routes ({data.convoyRoutes.length}):</div>
              {data.convoyRoutes.map((route, index) => (
                <RouteItem key={route.id} $isPaused={route.isPaused} $isHighlighted={highlightedRoutes.includes(route.id)}>
                  <RouteInfo>
                    <RouteName>
                      {route.isPaused && <AlertCircle size={10} style={{ display: "inline", marginRight: 4 }} />}
                      {route.name}
                    </RouteName>
                    {route.description && <div style={{ fontSize: 10, opacity: 0.8 }}>{route.description}</div>}
                  </RouteInfo>
                  {route.targetServiceName && (
                    <RouteTarget>
                      <ArrowRight size={10} />
                      {route.targetServiceName}
                    </RouteTarget>
                  )}

                  {/* Individual handle for each route - positioned absolutely within the route item */}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={getRouteHandleId(route.id)}
                    style={{
                      background: route.isPaused ? "#6b7280" : "#10b981",
                      width: 10,
                      height: 10,
                      border: "2px solid white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      position: "absolute",
                      top: "50%",
                      transform: "translateY(-50%)",
                      right: -15,
                    }}
                    isConnectable={isConnectable && !route.isPaused}
                  />
                </RouteItem>
              ))}
            </RouteList>
          )}
        </NodeBody>
      </NodeContainer>
    </>
  );
});
